export const SEO_WEIGHTED_V2 = {
  name: "seo-weighted-v2" as const,
  version: 2 as const,
  description:
    "Weighted mobile lab score: Performance 45%, SEO 35%, Best Practices 20%. Accessibility is reported separately and does not change the overall score. This is a lab diagnostic, not a ranking prediction.",
  inputs: ["mobile.performance", "mobile.seo", "mobile.best-practices"] as const,
  weights: { performance: 0.45, seo: 0.35, bestPractices: 0.2, accessibility: 0 },
  unavailableDataRule:
    "Unavailable evidence is not converted to zero; incomplete evidence is retained as partial and unscored.",
};

/**
 * Only with SEO_LH_AGENTIC_SCORE on and an applicable Lighthouse Agentic
 * Browsing result: seo-weighted-v2 scaled to 90%, plus the agentic pass rate
 * at 10%. Otherwise SEO_WEIGHTED_V2 applies unchanged.
 */
export const SEO_WEIGHTED_V3_AGENTIC = {
  name: "seo-weighted-v3-agentic" as const,
  version: 3 as const,
  description:
    "Weighted mobile lab score: Performance 40.5%, SEO 31.5%, Best Practices 18%, Lighthouse Agentic Browsing pass rate 10%. Accessibility is reported separately and does not change the overall score. This is a lab diagnostic, not a ranking prediction.",
  inputs: ["mobile.performance", "mobile.seo", "mobile.best-practices", "mobile.agentic-browsing"] as const,
  weights: { performance: 0.405, seo: 0.315, bestPractices: 0.18, agenticBrowsing: 0.1, accessibility: 0 },
  unavailableDataRule:
    "Unavailable evidence is not converted to zero; when no Agentic Browsing audit applies, seo-weighted-v2 is used.",
};

export function computeOverallScore(categories: {
  performance: number;
  seo: number;
  bestPractices: number;
  /** 0-100 pass rate; pass only when SEO_LH_AGENTIC_SCORE is on. null/undefined keeps v2. */
  agenticBrowsing?: number | null;
}): number {
  if (typeof categories.agenticBrowsing === "number" && Number.isFinite(categories.agenticBrowsing)) {
    const { performance, seo, bestPractices, agenticBrowsing } = SEO_WEIGHTED_V3_AGENTIC.weights;
    return Math.round(
      categories.performance * performance +
        categories.seo * seo +
        categories.bestPractices * bestPractices +
        categories.agenticBrowsing * agenticBrowsing,
    );
  }
  const { performance, seo, bestPractices } = SEO_WEIGHTED_V2.weights;
  return Math.round(
    categories.performance * performance +
      categories.seo * seo +
      categories.bestPractices * bestPractices,
  );
}

/** Methodology recorded on the report; v3 only when the agentic input was used. */
export function scoreMethodologyFor(agenticScored: boolean) {
  const method = agenticScored ? SEO_WEIGHTED_V3_AGENTIC : SEO_WEIGHTED_V2;
  return {
    name: method.name,
    version: method.version,
    description: method.description,
    inputs: [...method.inputs],
    weights: method.weights,
    unavailableDataRule: method.unavailableDataRule,
  };
}
